import pluralize from 'pluralize'
import React, { useEffect, useState } from 'react'

import { IllustrationName } from '@/components/icons/Illustration'
import { useSummaryEditor } from '@/components/quotes/summary/summaryPopover'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { cn } from '../../lib/utils'
import { type Driver, useDrivers } from '../../models/driver'
import { ChatActionWithValue } from '../../models/insuranceApplication'
import { isFleetReadonly, useCurrentUser } from '../../models/user'
import { DriversTable } from '../DriversTable'
import { IllustratedRow } from './ConfirmInfoCard'

export interface ChatAddDriversProps {
  /** Review affordance rather than the step's own question — confirming then answers nothing. */
  edit: boolean
  slim?: boolean
  /**
   * Extra action rendered under the roster row in slim mode, given the same
   * opener the row uses — lets a host add its own CTA (e.g. "Add driver" on the
   * quotes page) without duplicating the dialog or the drivers table.
   */
  renderFooter?: (open: () => void) => React.ReactNode
  /** Answers the step. Left off wherever the roster is only being reviewed. */
  handleSubmit?: (data: ChatActionWithValue | FormData) => void
  orgId: string | undefined
  readonly?: boolean
  /** Onboarding confirmation step only: requires the fields carriers need. */
  enforceCarrierRequired?: boolean
}

export function ChatAddDrivers({
  handleSubmit,
  edit,
  slim,
  orgId,
  readonly,
  renderFooter,
  enforceCarrierRequired,
}: ChatAddDriversProps) {
  const { drivers: dbDrivers, isLoading, refetch } = useDrivers({ orgId, shouldFetchAllPages: true })
  const { user } = useCurrentUser({ enabled: false })
  // Closing the dialog also closes the summary popover it was opened from, where there is one.
  const dialog = useSummaryEditor()
  const [drivers, setDrivers] = useState<Driver[]>([])
  const isDisabled = readonly || isFleetReadonly(user)

  useEffect(() => setDrivers(dbDrivers), [dbDrivers])

  async function onConfirm() {
    await refetch()
    // Outside its own question this would answer whatever step the application is on.
    if (!edit) {
      handleSubmit?.({
        content: {
          component: 'ADD_DRIVERS',
          content: ``,
          props: {
            edit: true,
            disableEnteringAnimation: true,
          },
        },
        value: {
          name: 'ADD_DRIVERS',
        },
      })
    }
    dialog.close()
  }

  const row = (
    <IllustratedRow
      illustration={IllustrationName.DRIVER}
      label={
        isLoading
          ? 'Loading drivers...'
          : `${drivers.length} active ${pluralize('driver', drivers.length)} ${edit || !drivers.length ? '' : 'found'}`
      }
      action={
        !isDisabled &&
        // Nothing on file to edit — the host's own CTA ("Add driver") is the way in instead.
        (edit ? (
          (drivers.length > 0 || !renderFooter) && <Button variant={slim ? 'outline' : 'link'}>Edit</Button>
        ) : (
          <Button>{drivers.length ? 'Review' : 'Add'}</Button>
        ))
      }
    />
  )

  const dialogElement = (
    <Dialog open={dialog.isOpen} onOpenChange={dialog.setOpen}>
      <DialogContent size="full">
        <div>
          {!orgId || isLoading ? (
            <div className="text-muted-foreground py-10 text-center text-sm">Loading drivers…</div>
          ) : (
            <DriversTable
              drivers={drivers}
              setDrivers={setDrivers}
              handleSubmit={() => onConfirm()}
              orgId={orgId}
              enforceCarrierRequired={enforceCarrierRequired}
              footer={
                <div className="flex items-center justify-center gap-2">
                  <Button variant="link" onClick={dialog.close}>
                    Cancel
                  </Button>
                </div>
              }
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )

  if (slim) {
    return (
      <>
        <div
          className={cn('flex w-full cursor-pointer', isDisabled && 'cursor-auto opacity-50')}
          onClick={isDisabled ? undefined : dialog.open}
        >
          {row}
        </div>
        {!isDisabled && renderFooter?.(dialog.open)}
        {dialogElement}
      </>
    )
  }

  return (
    <>
      <div
        className={cn(
          'mt-6 flex w-full cursor-pointer items-center justify-end gap-2 font-semibold',
          isDisabled && 'cursor-auto opacity-50'
        )}
        onClick={isDisabled ? undefined : dialog.open}
      >
        <Card className="w-full bg-transparent">
          <CardContent>{row}</CardContent>
        </Card>
      </div>
      {dialogElement}
    </>
  )
}
